import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { RecordedSave } from "@/admin/Recorded";
import { Table, Pill } from "@/admin/AdminLayout";

const ST: [string, string][] = [["submitted", "New"], ["reviewing", "Reviewing"], ["label_sent", "Label sent"], ["covered", "Covered"], ["quoted", "Quoted"], ["resolved", "Resolved"], ["declined", "Declined"]];
const label = (s: string) => ST.find(([k]) => k === s)?.[1] || s;

function MediaThumb({ m }: { m: any }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/admin-x/warranty/media/${m.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [m.id]);
  if (!url) return <span className="w-14 h-14 bg-neutral-100 inline-block" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="w-14 h-14 bg-neutral-100 inline-grid place-items-center overflow-hidden" title={m.name} data-testid={`claim-admin-media-${m.id}`}>
      {m.kind === "photo" ? <img src={url} alt="" className="w-full h-full object-cover" /> : <Video size={18} />}
    </a>
  );
}

function StatusEditor({ c, onDone }: { c: any; onDone: () => void }) {
  const [status, setStatus] = useState(c.status);
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(c.quote || "");
  const [notify, setNotify] = useState(true);
  const save = async () => {
    try { await api.put(`/admin-x/warranty/${c.id}`, { status, note: note || undefined, quote: quote ? Number(quote) : undefined, notify }); toast(notify ? "Updated — email to the customer is on its way (see the email log)" : "Updated"); setNote(""); window.setTimeout(onDone, 1500); return true;  }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  return (
    <div className="space-y-1.5 min-w-[220px]">
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-neutral-300 text-xs px-2 py-1 w-full" data-testid={`claim-status-${c.id}`}>{ST.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      {status === "quoted" && <input type="number" min="0" value={quote} onChange={(e) => setQuote(e.target.value)} placeholder="Quote A$" className="border border-neutral-300 text-xs px-2 py-1 w-full" data-testid={`claim-quote-${c.id}`} />}
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Message to customer (optional)" className="border border-neutral-300 text-xs px-2 py-1 w-full" data-testid={`claim-note-${c.id}`} />
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] flex items-center gap-1"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid={`claim-notify-${c.id}`} />Email customer</label>
        <RecordedSave res="warranty_claims" id={c.id} onSave={save} value={{ status, note, quote }} testId={`claim-save-${c.id}`} />
      </div>
    </div>
  );
}

export function WarrantyInbox({ onChange }: { onChange?: () => void }) {
  const [items, setItems] = useState<any[] | null>(null);
  const load = () => api.get("/admin-x/warranty").then((r) => { setItems(r.data); onChange?.(); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items) return null;
  if (!items.length) return <p className="text-sm text-neutral-500" data-testid="warranty-inbox-empty">No warranty claims yet.</p>;
  return (
    <Table head={["Claim", "Customer", "Piece & issue", "Photos / videos", "Status", "Update"]} testId="warranty-inbox">
      {items.map((c) => (
        <tr key={c.id} className="align-top" data-testid={`claim-row-${c.id}`}>
          <td className="px-4 py-3 text-xs whitespace-nowrap"><strong>{c.number}</strong><div className="text-neutral-500">{new Date(c.submitted_at || c.created_at).toLocaleString("en-AU")}</div><div>Order {c.order_number}</div></td>
          <td className="px-4 py-3 text-xs">{c.name}<div className="text-neutral-500"><a href={`mailto:${c.email}`} className="underline">{c.email}</a>{c.phone ? ` · ${c.phone}` : ""}</div></td>
          <td className="px-4 py-3 text-xs max-w-xs"><strong>{c.item}</strong><p className="mt-1 whitespace-pre-line text-neutral-700">{c.issue}</p></td>
          <td className="px-4 py-3"><div className="flex flex-wrap gap-1.5 max-w-[200px]">{(c.media || []).map((m: any) => <MediaThumb key={m.id} m={m} />)}</div></td>
          <td className="px-4 py-3 text-xs"><Pill tone={["covered", "resolved"].includes(c.status) ? "green" : c.status === "submitted" ? "amber" : "neutral"}>{label(c.status)}</Pill>{c.quote ? <div className="mt-1">Quote A${Number(c.quote).toLocaleString()}</div> : null}
            <ul className="mt-2 space-y-0.5 text-[10px] text-neutral-500">{(c.timeline || []).slice(-4).map((t: any, i: number) => <li key={i}>{new Date(t.at).toLocaleDateString("en-AU")} · {label(t.status)}{t.note ? ` — ${t.note}` : ""}</li>)}</ul></td>
          <td className="px-4 py-3"><StatusEditor c={c} onDone={load} /></td>
        </tr>
      ))}
    </Table>
  );
}
